import { useEffect, useState } from 'react';
import { ENDPOINTS } from './api.js';

/**
 * Public server config (`/api/config/`) — feature flags the UI needs to respect.
 *
 * Served by the backend rather than baked in as REACT_APP_* vars so a flag can be flipped with a
 * backend restart (no frontend rebuild), and the two can never disagree about what's enabled.
 *
 * Cached at module level with a shared in-flight promise, so the endpoint is hit once per page
 * load no matter how many components ask.
 */
let cache = null;
let inflight = null;

/** Test-only: clear the module-level cache so each test starts fresh.
 *  (Re-importing the module under jest.resetModules() would load a second React copy.) */
export function __resetAppConfigCache() {
  cache = null;
  inflight = null;
}

export function useAppConfig() {
  const [config, setConfig] = useState(cache);

  useEffect(() => {
    if (cache) return undefined;
    let alive = true;
    if (!inflight) {
      inflight = fetch(ENDPOINTS.CONFIG)
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({}));   // a config failure must never break the page
    }
    inflight.then((d) => {
      cache = d || {};
      if (alive) setConfig(cache);
    });
    return () => { alive = false; };
  }, []);

  // Default to the SAFE state while loading: features stay hidden until the server says otherwise,
  // so a disabled feature never flashes into view.
  return config || {};
}

export default useAppConfig;
